import { lazy } from 'react';
import ConversionLayout from '~/components/Layout/ConversionLayout';
import Layout from '~/components/Layout';
import type { PreRoute } from '~/models/route';

// auth
const Offline = lazy(() => import('../pages/offline'));
const Private = lazy(() => import('../components/Private'));
const Protected = lazy(() => import('../components/Protected'));
const Login = lazy(() => import('../pages/login'));
const LoginGoogle = lazy(() => import('../pages/login_google'));
const Logout = lazy(() => import('../pages/logout'));
const VerifyEmail = lazy(() => import('../pages/verify_email'));
const ResetPassword = lazy(() => import('../pages/reset_password'));
const ConfirmPassword = lazy(() => import('../pages/confirm_password'));
const Documents = lazy(() => import('../pages/documents'));
const News = lazy(() => import('../pages/news'));
const Document = lazy(() => import('../pages/document'));
const Link = lazy(() => import('../pages/link'));
const EditLink = lazy(() => import('../pages/edit_link'));
const DeleteLink = lazy(() => import('../pages/delete_link'));
const CreateLink = lazy(() => import('../pages/create_link'));
const DocumentsCreate = lazy(() => import('../pages/create_document'));
const CreateNews = lazy(() => import('../pages/create_news'));
const EditNews = lazy(() => import('../pages/edit_news'));
const DeleteNews = lazy(() => import('../pages/delete_news'));
const NewsSingle = lazy(() => import('../pages/news_single'));
const DocumentsEdit = lazy(() => import('../pages/edit_document'));
const DocumentsDelete = lazy(() => import('../pages/delete_document'));
const Forms = lazy(() => import('../pages/forms'));
const Form = lazy(() => import('../pages/form'));
const FormResults = lazy(() => import('../pages/form_results'));
const FormResult = lazy(() => import('../pages/form_result'));
const Links = lazy(() => import('../pages/links'));
const ChangeEmail = lazy(() => import('../pages/change_email'));
const PrivacyPolicy = lazy(() => import('../pages/privacy-policy'));
const Terms = lazy(() => import('../pages/terms'));
// const VerifyEmail = lazy(() => import('../pages/verify_email'));
// const Register = lazy(() => import('../pages/register'));
// const MagicLink = lazy(() => import('../pages/magic_link'));

// other
const Home = lazy(() => import('../pages/index'));
const Reference = lazy(() => import('../pages/reference'));

// settings
const Account = lazy(() => import('../pages/settings/account'));
const Users = lazy(() => import('../pages/settings/users'));
const User = lazy(() => import('../pages/settings/user'));
const CreateUser = lazy(() => import('../pages/settings/create_user'));
const EditUser = lazy(() => import('../pages/settings/edit_user'));
const DeleteUser = lazy(() => import('../pages/settings/delete_user'));
const SettingsOverview = lazy(() => import('../pages/settings/settings'));

const Invite = lazy(() => import('../pages/invite'));
const BillingTest = lazy(() => import('../pages/change-plan'));
const Concierge = lazy(() => import('../pages/concierge'));

const routes: PreRoute[] = [
	{
		path: '/reference',
		element: <Reference />,
	},
	// {
	// 	path: '/magic-link',
	// 	element: <MagicLink />,
	// },
	{
		path: '/change-email',
		element: <ChangeEmail />,
	},
	{
		path: '/verify-email',
		element: <VerifyEmail />,
	},
	{
		path: '/invite',
		element: <ConversionLayout />,
		disableHeader: true,
		children: [
			{
				index: true,
				element: <Concierge />,
			},
		],
	},
	{
		path: '/login',
		element: <ConversionLayout />,
		header: {
			isHidden: true,
		},
		children: [
			{
				index: true,
				element: <Login />,
			},
		],
	},

	{
		path: '/login/social',
		element: <ConversionLayout />,
		header: {
			isHidden: true,
		},
		children: [
			{
				index: true,
				element: <LoginGoogle />,
			},
		],
	},
	{
		path: '/logout',
		element: <ConversionLayout />,
		header: {
			isHidden: true,
		},
		children: [
			{
				index: true,
				element: <Logout />,
			},
		],
	},

	{
		path: '/forgot-password',
		element: <ConversionLayout />,
		header: {
			isHidden: true,
		},
		children: [
			{
				index: true,
				element: <ResetPassword />,
			},
		],
	},
	{
		path: '/forgot-password-confirm',
		element: <ConversionLayout />,
		header: {
			isHidden: true,
		},
		children: [
			{
				index: true,
				element: <ConfirmPassword />,
			},
		],
	},

	{
		element: <Private />,
		children: [
			{
				path: '/',
				element: <Layout />,
				children: [
					{
						index: true,
						element: <Home />,
					},
					{
						path: '/admin',
						element: <Protected permissionSets={[['admin']]} />,
						crumb: 'Admin',
						children: [
							{
								index: true,
								element: <div>Admin</div>,
							},
						],
					},
					{
						header: {
							container: 'lg',
						},
						mainContent: {
							container: 'lg',
						},

						path: '/account',
						crumb: 'Account',
						element: <Account />,
					},

					{
						crumb: 'Settings',
						path: '/settings',
						children: [
							{
								index: true,
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								element: <SettingsOverview />,
							},
						],
					},
					{
						header: {
							container: 'lg',
						},
						mainContent: {
							container: 'lg',
						},

						path: '/users',
						crumb: 'Users',
						children: [
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								index: true,
								crumb: 'Users',
								element: <Users />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								path: '/users/create',
								crumb: 'Create',
								element: <CreateUser />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								path: '/users/delete/:id',
								crumb: 'Delete',
								element: <DeleteUser />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								path: '/users/edit/:id',
								crumb: 'Edit',
								element: <EditUser />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								path: '/users/:userId',
								crumb: 'User',
								element: <User />,
							},
						],
					},
					{
						crumb: 'Forms',
						path: '/forms',
						children: [
							{
								index: true,
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								element: <Forms />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								path: '/forms/results/:id',
								crumb: 'Form',
								element: <FormResults />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								path: '/forms/results/:id/:resultsId',
								crumb: 'Form',
								element: <FormResult />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								path: '/forms/:id',
								crumb: 'Form',
								element: <Form />,
							},
						],
					},
					{
						crumb: 'Documents',
						path: '/documents',
						children: [
							{
								index: true,
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								element: <Documents />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/documents/create',
								crumb: 'Create',
								element: <DocumentsCreate />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/documents/edit/:id',
								crumb: 'Edit',
								element: <DocumentsEdit />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/documents/delete/:id',
								crumb: 'Delete',
								element: <DocumentsDelete />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/documents/:id',
								crumb: 'Document',
								element: <Document />,
							},
						],
					},
					//news
					{
						crumb: 'News',
						path: '/news',
						children: [
							{
								index: true,
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								element: <News />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/news/create',
								crumb: 'Create',
								element: <CreateNews />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/news/edit/:id',
								crumb: 'Edit',
								element: <EditNews />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/news/delete/:id',
								crumb: 'Delete',
								element: <DeleteNews />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/news/:id',
								crumb: 'news',
								element: <NewsSingle />,
							},
						],
					},
					//terms
					{
						crumb: 'Terms',
						path: '/terms-and-conditions',
						children: [
							{
								index: true,
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								element: <Terms />,
							},
						],
					},
					{
						crumb: 'Privacy',
						path: '/privacy-policy',
						children: [
							{
								index: true,
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								element: <PrivacyPolicy />,
							},
						],
					},
					{
						crumb: 'Links',
						path: '/links',
						children: [
							{
								index: true,
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},
								element: <Links />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/links/create',
								crumb: 'Create',
								element: <CreateLink />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/links/delete/:id',
								crumb: 'Delete',
								element: <DeleteLink />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/links/edit/:id',
								crumb: 'Link',
								element: <EditLink />,
							},
							{
								header: {
									container: 'lg',
								},
								mainContent: {
									container: 'lg',
								},

								path: '/links/:id',
								crumb: 'Link',
								element: <Link />,
							},
						],
					},

					{
						path: '/offline',
						element: <Offline />,
						crumb: 'Offline',
					},
					{
						path: '/offline',
						element: <Offline />,
						crumb: 'Offline',
					},
				],
			},
		],
	},
];

interface INavigation {
	name: string;
	path: string;
}

export const navigation: INavigation[] = [
	{
		name: 'Home',
		path: '/',
	},
	{
		name: 'Forms',
		path: '/forms',
	},
	{
		name: 'Links',
		path: '/links',
	},
	{
		name: 'Documents',
		path: '/documents',
	},
	{
		name: 'Settings',
		path: '/settings',
	},
];

export default routes;
